import {
  Component,
  OnInit,
  OnDestroy,
  Input,
  Output,
  EventEmitter,
  inject,
  ChangeDetectorRef,
  HostListener,
  ElementRef,
  ViewChildren,
  QueryList,
} from '@angular/core';
import { Subscription } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { CommonModule } from '@angular/common';
import { FormBuilder, ReactiveFormsModule, FormsModule, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import {
  AclApdApiService,
  AuthApiService,
  CatalogueApiService,
  PolicyCreatePayload,
  PolicyCreateRequestItem,
  SearchCriteria,
  istToUtc,
  parsePolicyExpiryIsoToMillis,
} from '@angular-workspace/services';
import { resolveCatalogueApdUrl } from '@angular-workspace/feature';
import { environment } from '../../../environments/environment';

import { HeaderComponent } from '../../shared/components/header/header.component';
import { OrgAdminSidebarComponent } from '../sidebar/org-admin-sidebar.component';
import { ProviderSidebarComponent } from '../../provider/sidebar/provider-sidebar.component';
import { PageHeaderComponent } from '../../shared/components/page-header/page-header.component';
import { ToasterService } from '../../services/toaster.service';
import {
  MultiselectComponent,
  SingleSelectComponent,
  DateRangePickerComponent,
  DateRange,
} from '../../shared/components/primeng';
import { TimePickerComponent } from '../../shared/components/primeng/time-picker/time-picker.component';

export type PolicyAssetKind = 'dataset' | 'model';
export type PolicyTargetRole = 'provider' | 'org-admin' | 'all';
export type PolicyScopeType = 'group' | 'individual';

export interface PolicyOrganisationOption {
  label: string;
  value: string;
}

export interface PolicyRoleOption {
  label: string;
  value: PolicyTargetRole;
}

export interface PolicyAssetOption {
  id: string;
  label: string;
  organization?: string;
  /** Constraint-relevant access types from asset `resourceServer[].accessTypes` (`api` | `file`). */
  accessTypes?: string[];
}

export interface PolicyAssetKindOption {
  label: string;
  value: PolicyAssetKind;
}

export interface PolicyIndividualEmailOption {
  label: string;
  value: string;
  /** Consumer id from basic user API (`sub`). */
  userId?: string;
}

export interface PolicyConstraintRow {
  key: 'constraintApi' | 'constraintFile';
  name: string;
  enabledKey: 'constraintApi' | 'constraintFile';
  accessType: 'api' | 'file';
  dateKey: 'apiExpiryDate' | 'fileExpiryDate';
  timeKey: 'apiExpiryTime' | 'fileExpiryTime';
}

/** Persisted add-policy wizard state (sessionStorage) for refresh recovery. */
interface AddPolicyWizardDraft {
  currentStep: number;
  form: {
    scopeType: PolicyScopeType;
    organisationIds: string[];
    targetRoles: PolicyTargetRole[];
    individualName: string;
    individualEmail: string;
    assetKind: PolicyAssetKind | null;
    selectedAssetId: string;
    constraintApi: boolean;
    constraintFile: boolean;
    apiExpiryDate: string;
    apiExpiryTime: string;
    fileExpiryDate: string;
    fileExpiryTime: string;
    providerComment: string;
    feedbackComment: string;
  };
  organisationSelection: PolicyOrganisationOption[];
  roleSelection: PolicyRoleOption[];
  assetSearchQuery: string;
  selectedAssetAccessTypes: string[];
  /** @deprecated draft key; restored into selectedAssetAccessTypes */
  selectedAssetResourceServerTypes?: string[];
  lastVerifiedIndividualEmail: string;
  individualEmailOptions?: PolicyIndividualEmailOption[];
}

@Component({
  selector: 'app-add-policy-home',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    HeaderComponent,
    OrgAdminSidebarComponent,
    ProviderSidebarComponent,
    PageHeaderComponent,
    MultiselectComponent,
    SingleSelectComponent,
    DateRangePickerComponent,
    TimePickerComponent,
  ],
  templateUrl: './add-policy-home.component.html',
  styleUrl: './add-policy-home.component.scss',
})
export class AddPolicyHomeComponent implements OnInit, OnDestroy {
  /** When true, render wizard only (no header/sidebar) for use inside a modal. */
  @Input() embedded = false;
  @Output() policySaved = new EventEmitter<void>();
  @Output() cancelled = new EventEmitter<void>();

  @ViewChildren(MultiselectComponent) multiselectComponents!: QueryList<MultiselectComponent>;
  @ViewChildren(DateRangePickerComponent) dateRangePickerComponents!: QueryList<DateRangePickerComponent>;
  @ViewChildren(TimePickerComponent) timePickerComponents!: QueryList<TimePickerComponent>;

  private readonly fb = inject(FormBuilder);
  private readonly router = inject(Router);
  private readonly catalogueApi = inject(CatalogueApiService);
  private readonly authApi = inject(AuthApiService);
  private readonly aclApdApi = inject(AclApdApiService);
  private readonly toaster = inject(ToasterService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly elementRef = inject(ElementRef);

  readonly assetKindOptions: PolicyAssetKindOption[] = [
    { label: 'Dataset', value: 'dataset' },
    { label: 'Model', value: 'model' },
  ];

  readonly roleOptions: PolicyRoleOption[] = [
    { label: 'Provider', value: 'provider' },
    { label: 'Org Admin', value: 'org-admin' },
    { label: 'Consumer', value: 'all' },
  ];

  readonly constraintRows: PolicyConstraintRow[] = [
    {
      key: 'constraintApi',
      name: 'API',
      enabledKey: 'constraintApi',
      accessType: 'api',
      dateKey: 'apiExpiryDate',
      timeKey: 'apiExpiryTime',
    },
    {
      key: 'constraintFile',
      name: 'File',
      enabledKey: 'constraintFile',
      accessType: 'file',
      dateKey: 'fileExpiryDate',
      timeKey: 'fileExpiryTime',
    },
  ];

  readonly form = this.fb.group({
    scopeType: this.fb.nonNullable.control<PolicyScopeType>('group'),
    organisationIds: this.fb.control<string[]>([]),
    targetRoles: this.fb.control<PolicyTargetRole[]>([]),
    individualName: [''],
    individualEmail: ['', [Validators.email]],
    assetKind: this.fb.control<PolicyAssetKind | null>(null, { validators: [Validators.required] }),
    selectedAssetId: ['', Validators.required],
    constraintApi: this.fb.nonNullable.control(false),
    constraintFile: this.fb.nonNullable.control(false),
    apiExpiryDate: [''],
    apiExpiryTime: [{ value: '', disabled: true }],
    fileExpiryDate: [''],
    fileExpiryTime: [{ value: '', disabled: true }],
    providerComment: ['', Validators.required],
    feedbackComment: ['', Validators.required],
  });

  organisationOptions: PolicyOrganisationOption[] = [];
  organisationsLoading = false;

  assetSearchQuery = '';
  assetSuggestions: PolicyAssetOption[] = [];
  selectedAssetAccessTypes: string[] = [];
  showAssetAutocomplete = false;
  assetsSearching = false;
  assetSearchAttempted = false;
  individualEmailLookupLoading = false;
  individualEmailOptions: PolicyIndividualEmailOption[] = [];
  /** True only after by-identifier API returns HTTP 404 (drives field error UI). */
  individualEmailNotFound = false;
  /** True only after by-identifier API returns HTTP 400 Bad Request (invalid email). */
  individualEmailInvalidFormat = false;

  /** Stable references for multiselect ngModel (avoid getter → infinite CD loop). */
  organisationSelection: PolicyOrganisationOption[] = [];
  roleSelection: PolicyRoleOption[] = [];
  assetKindSelection: PolicyAssetKindOption[] = [];

  readonly rowExpiryDateRangeModels: Record<PolicyConstraintRow['dateKey'], DateRange> = {
    apiExpiryDate: { startDate: null, endDate: null },
    fileExpiryDate: { startDate: null, endDate: null },
  };

  private searchDebounceTimer: ReturnType<typeof setTimeout> | null = null;
  private individualEmailLookupTimer: ReturnType<typeof setTimeout> | null = null;
  private wizardDraftSub: Subscription | null = null;
  private wizardDraftRestored = false;
  private lastVerifiedIndividualEmail = '';
  private selectedIndividualUserId = '';
  isSubmitting = false;
  currentStep = 1;
  readonly totalSteps = 4;

  readonly wizardSteps: readonly { label: string }[] = [
    { label: 'Policy scope' },
    { label: 'Target asset' },
    { label: 'Constraints' },
    { label: 'Comments' },
  ];

  /** Earliest selectable expiry date (today). */
  get constraintExpiryMinDate(): Date {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return today;
  }

  ngOnInit(): void {
    const preserveDraftForReload = this.consumeReloadPreserveDraftFlag();
    const restored = preserveDraftForReload && this.restoreWizardDraft();
    this.updateScopeValidators();
    this.syncAssetKindSelection();
    if (restored) {
      const roles = this.form.get('targetRoles')?.value ?? [];
      this.roleSelection = this.toCanonicalRoleSelection(roles);
    }
    void this.loadOrganisations();
    this.syncAllRowExpiryDateRangeModels();
    this.syncConstraintAvailability();
    this.syncConstraintExpiryFieldStates();
    this.wizardDraftSub = this.form.valueChanges
      .pipe(debounceTime(200))
      .subscribe(() => this.persistWizardDraft());
  }

  ngOnDestroy(): void {
    this.wizardDraftSub?.unsubscribe();
    this.wizardDraftSub = null;
    if (!this.shouldPreserveDraftForReload()) {
      this.clearWizardDraft();
    }
  }

  @HostListener('window:beforeunload')
  onBeforeUnload(): void {
    this.markDraftForReloadPreserve();
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    const assetWrapper = this.elementRef.nativeElement.querySelector('.add-policy-home__asset-search');
    if (assetWrapper && !assetWrapper.contains(target)) {
      this.showAssetAutocomplete = false;
    }
  }

  get isOrgAdminRoute(): boolean {
    return this.router.url.includes('/org-admin/');
  }

  get isProviderRoute(): boolean {
    return !this.isOrgAdminRoute;
  }

  get hasAssetKindSelected(): boolean {
    return this.form.get('assetKind')?.value != null;
  }

  get assetKindLabel(): string {
    const kind = this.form.get('assetKind')?.value;
    if (kind === 'model') {
      return 'Model';
    }
    if (kind === 'dataset') {
      return 'Dataset';
    }
    return 'Asset';
  }

  get isGroupScope(): boolean {
    return this.form.get('scopeType')?.value === 'group';
  }

  get isIndividualScope(): boolean {
    return this.form.get('scopeType')?.value === 'individual';
  }

  get showIndividualEmailSelectionHint(): boolean {
    return (
      this.isIndividualScope &&
      !this.individualEmailLookupLoading &&
      this.individualEmailOptions.length > 1
    );
  }

  get individualEmailPlaceholder(): string {
    if (this.individualEmailLookupLoading) {
      return 'Verifying user...';
    }
    if (this.individualEmailOptions.length) {
      return 'Select email address';
    }
    return 'Validate user name first';
  }

  get isStepOneContinueDisabled(): boolean {
    if (this.isGroupScope) {
      const organisationIds = this.form.get('organisationIds')?.value ?? [];
      const targetRoles = this.form.get('targetRoles')?.value ?? [];
      return organisationIds.length === 0 || targetRoles.length === 0;
    }
    const email = String(this.form.get('individualEmail')?.value ?? '').trim();
    if (!email) {
      return true;
    }
    if (this.individualEmailLookupLoading) {
      return true;
    }
    if (this.lastVerifiedIndividualEmail !== email) {
      return true;
    }
    return !!this.form.get('individualEmail')?.invalid;
  }

  get showValidateIndividualEmailButton(): boolean {
    if (!this.isIndividualScope) {
      return false;
    }
    return !!String(this.form.get('individualEmail')?.value ?? '').trim();
  }

  get showInvalidEmailMessage(): boolean {
    return !!(
      this.isIndividualScope &&
      !this.individualEmailLookupLoading &&
      this.individualEmailNotFound
    );
  }

  get showInvalidEmailFormatMessage(): boolean {
    return !!(
      this.isIndividualScope &&
      !this.individualEmailLookupLoading &&
      this.individualEmailInvalidFormat
    );
  }

  get showIndividualEmailFieldError(): boolean {
    return this.showInvalidEmailMessage || this.showInvalidEmailFormatMessage;
  }

  get isValidateIndividualEmailButtonDisabled(): boolean {
    if (!this.isIndividualScope) {
      return true;
    }
    if (this.individualEmailLookupLoading) {
      return true;
    }
    const emailCtrl = this.form.get('individualEmail');
    const email = String(emailCtrl?.value ?? '').trim();
    if (!email) {
      return true;
    }
    return !!emailCtrl?.hasError('email') || !!emailCtrl?.hasError('required');
  }

  get isIndividualEmailVerified(): boolean {
    if (!this.isIndividualScope || this.individualEmailLookupLoading) {
      return false;
    }
    const emailCtrl = this.form.get('individualEmail');
    const email = String(emailCtrl?.value ?? '').trim();
    if (!email || this.lastVerifiedIndividualEmail !== email) {
      return false;
    }
    return !emailCtrl?.invalid;
  }

  get isStepTwoContinueDisabled(): boolean {
    const assetKind = this.form.get('assetKind')?.value;
    if (!assetKind) {
      return true;
    }
    const assetId = String(this.form.get('selectedAssetId')?.value ?? '').trim();
    return !assetId;
  }

  get visibleConstraintRows(): PolicyConstraintRow[] {
    const kind = this.form.get('assetKind')?.value;
    if (kind === 'model') {
      return this.constraintRows.filter((row) => row.accessType === 'file');
    }
    const types = this.selectedAssetAccessTypes;
    if (!types.length) {
      return this.constraintRows;
    }
    const showApi = types.includes('api');
    const showFile = types.includes('file');
    if (showApi && showFile) {
      return this.constraintRows;
    }
    if (showApi) {
      return this.constraintRows.filter((row) => row.accessType === 'api');
    }
    if (showFile) {
      return this.constraintRows.filter((row) => row.accessType === 'file');
    }
    return this.constraintRows;
  }

  get isStepThreeContinueDisabled(): boolean {
    const hasAnyConstraintSelected = this.visibleConstraintRows.some(
      (row) => !!this.form.get(row.enabledKey)?.value,
    );
    if (!hasAnyConstraintSelected) {
      return true;
    }
    for (const row of this.visibleConstraintRows) {
      if (!this.form.get(row.enabledKey)?.value) {
        continue;
      }
      const dateStr = String(this.form.get(row.dateKey)?.value ?? '').trim();
      if (!dateStr || this.isExpiryDateBeforeToday(dateStr)) {
        return true;
      }
      const timeStr = String(this.form.get(row.timeKey)?.value ?? '').trim();
      if (!timeStr) {
        return true;
      }
      if (this.isExpiryDateTimeInPast(dateStr, timeStr)) {
        return true;
      }
    }
    const accessPreview = this.buildAccessConstraints(this.form.getRawValue());
    return !this.resolvePolicyExpiryIso(accessPreview);
  }

  onScopeTypeChange(type: PolicyScopeType): void {
    if (this.form.get('scopeType')?.value === type) {
      return;
    }
    this.form.patchValue({ scopeType: type });
    this.clearOrganisationSelection();

    if (type === 'group') {
      this.form.patchValue({ individualName: '', individualEmail: '' });
      this.lastVerifiedIndividualEmail = '';
      this.selectedIndividualUserId = '';
      this.individualEmailOptions = [];
      this.clearIndividualEmailLookupUiState();
      void this.applyDefaultOrganisationSelection();
    } else {
      this.roleSelection = [];
      this.form.patchValue({ targetRoles: [], individualName: '' });
      this.clearIndividualEmailLookupUiState();
    }
    this.updateScopeValidators();
    this.updateIndividualEmailFieldState();
    this.cdr.markForCheck();
  }

  private updateScopeValidators(): void {
    const scopeType = this.form.get('scopeType')?.value ?? 'group';
    const orgCtrl = this.form.get('organisationIds');
    const rolesCtrl = this.form.get('targetRoles');
    const nameCtrl = this.form.get('individualName');
    const emailCtrl = this.form.get('individualEmail');

    if (scopeType === 'group') {
      orgCtrl?.setValidators([Validators.required]);
      rolesCtrl?.setValidators([Validators.required]);
      nameCtrl?.clearValidators();
      nameCtrl?.setValue('', { emitEvent: false });
      emailCtrl?.clearValidators();
      emailCtrl?.setValue('', { emitEvent: false });
      emailCtrl?.enable({ emitEvent: false });
    } else {
      orgCtrl?.clearValidators();
      rolesCtrl?.clearValidators();
      nameCtrl?.clearValidators();
      nameCtrl?.setValue('', { emitEvent: false });
      emailCtrl?.setValidators([Validators.required, Validators.email]);
      emailCtrl?.enable({ emitEvent: false });
    }

    nameCtrl?.updateValueAndValidity({ emitEvent: false });
    orgCtrl?.updateValueAndValidity({ emitEvent: false });
    rolesCtrl?.updateValueAndValidity({ emitEvent: false });
    emailCtrl?.updateValueAndValidity({ emitEvent: false });
    this.updateIndividualEmailFieldState();
  }

  private updateIndividualEmailFieldState(): void {
    const emailCtrl = this.form.get('individualEmail');
    if (!emailCtrl) {
      return;
    }
    if (!this.isIndividualScope) {
      emailCtrl.enable({ emitEvent: false });
      return;
    }
    if (this.individualEmailLookupLoading) {
      emailCtrl.disable({ emitEvent: false });
    } else {
      emailCtrl.enable({ emitEvent: false });
    }
  }

  onIndividualEmailInput(): void {
    if (!this.isIndividualScope) {
      return;
    }
    const emailCtrl = this.form.get('individualEmail');
    const email = String(emailCtrl?.value ?? '').trim();

    this.lastVerifiedIndividualEmail = '';
    this.selectedIndividualUserId = '';
    this.individualEmailOptions = [];
    this.clearIndividualEmailLookupUiState();
    this.updateIndividualEmailFieldState();

    if (!email || emailCtrl?.hasError('email') || emailCtrl?.hasError('required')) {
      this.individualEmailLookupLoading = false;
      this.cdr.markForCheck();
    }
  }

  onClearIndividualEmail(): void {
    if (!this.isIndividualScope) {
      return;
    }

    this.form.patchValue({ individualEmail: '', individualName: '' }, { emitEvent: false });
    this.lastVerifiedIndividualEmail = '';
    this.selectedIndividualUserId = '';
    this.individualEmailOptions = [];
    this.clearIndividualEmailLookupUiState();
    this.individualEmailLookupLoading = false;
    this.form.get('individualEmail')?.markAsUntouched();
    this.updateIndividualEmailFieldState();
    this.cdr.markForCheck();
  }

  onValidateIndividualEmail(): void {
    if (!this.isIndividualScope || this.isValidateIndividualEmailButtonDisabled) {
      return;
    }
    const email = String(this.form.get('individualEmail')?.value ?? '').trim();
    if (!email) {
      return;
    }
    this.lastVerifiedIndividualEmail = '';
    this.selectedIndividualUserId = '';
    this.individualEmailOptions = [];
    this.clearIndividualEmailLookupUiState();
    void this.lookupIndividualEmail(email);
  }

  private async lookupIndividualEmail(email: string): Promise<void> {
    if (!this.isIndividualScope) return;
    this.individualEmailLookupLoading = true;
    this.individualEmailOptions = [];
    this.clearIndividualEmailLookupUiState();
    this.updateIndividualEmailFieldState();
    this.cdr.markForCheck();

    let lookupError: 'notFound' | 'invalidFormat' | null = null;
    try {
      const response = await this.authApi.getBasicUserByEmail(email);
      const currentEmail = String(this.form.get('individualEmail')?.value ?? '').trim();
      if (currentEmail !== email) {
        return;
      }
      const users = this.extractUserResults(response);
      const hasMatch = users.length > 0 || this.hasAnyUserResult(response);
      if (hasMatch) {
        const options = this.buildIndividualEmailOptions(users);
        const matchedOption =
          options.find((option) => option.value.toLowerCase() === email.toLowerCase()) ?? options[0];
        const userId =
          matchedOption?.userId ??
          (users.length > 0
            ? this.extractConsumerUserId(users[0])
            : this.extractUserId(response as Record<string, unknown>));
        if (!userId) {
          this.lastVerifiedIndividualEmail = '';
          this.selectedIndividualUserId = '';
          return;
        }
        this.individualEmailOptions = options;
        this.lastVerifiedIndividualEmail = email;
        this.selectedIndividualUserId = userId;
      } else {
        this.lastVerifiedIndividualEmail = '';
        this.selectedIndividualUserId = '';
      }
    } catch (error: unknown) {
      const currentEmail = String(this.form.get('individualEmail')?.value ?? '').trim();
      if (currentEmail !== email) {
        return;
      }
      this.lastVerifiedIndividualEmail = '';
      this.selectedIndividualUserId = '';
      const status = this.extractHttpStatus(error);
      // 404 → user not found; 400 → invalid email format from API.
      if (status === 404) {
        lookupError = 'notFound';
      } else if (status === 400) {
        lookupError = 'invalidFormat';
      }
    } finally {
      // enable() re-runs validators and would wipe custom FormControl errors set earlier —
      // apply lookup UI flags/errors only after the control is re-enabled.
      this.individualEmailLookupLoading = false;
      this.updateIndividualEmailFieldState();
      if (lookupError === 'notFound') {
        this.individualEmailNotFound = true;
        this.individualEmailInvalidFormat = false;
        this.setIndividualUserLookupError('emailNotFound', true);
      } else if (lookupError === 'invalidFormat') {
        this.individualEmailNotFound = false;
        this.individualEmailInvalidFormat = true;
        this.setIndividualUserLookupError('emailInvalidFormat', true);
      } else {
        this.clearIndividualEmailLookupUiState();
      }
      this.cdr.detectChanges();
    }
  }

  private clearIndividualEmailLookupUiState(): void {
    this.individualEmailNotFound = false;
    this.individualEmailInvalidFormat = false;
    this.clearIndividualUserLookupErrors();
  }

  /** Best-effort HTTP status from HttpErrorResponse / nested API error shapes. */
  private extractHttpStatus(error: unknown): number | null {
    if (!error || typeof error !== 'object') {
      return null;
    }
    const err = error as {
      status?: number | string;
      statusCode?: number | string;
      error?: { status?: number | string; statusCode?: number | string };
      response?: { status?: number | string };
    };
    const raw =
      err.status ??
      err.statusCode ??
      err.error?.status ??
      err.error?.statusCode ??
      err.response?.status;
    const status = typeof raw === 'string' ? Number(raw) : raw;
    return typeof status === 'number' && Number.isFinite(status) ? status : null;
  }

  private buildIndividualEmailOptions(users: Record<string, unknown>[]): PolicyIndividualEmailOption[] {
    const options: PolicyIndividualEmailOption[] = [];
    const seen = new Set<string>();
    for (const user of users) {
      const resolvedEmail = String(
        user['email'] ??
          user['emailId'] ??
          user['email_id'] ??
          user['userEmail'] ??
          user['mail'] ??
          '',
      ).trim();
      if (!resolvedEmail || !resolvedEmail.includes('@')) {
        continue;
      }
      const key = resolvedEmail.toLowerCase();
      if (seen.has(key)) {
        continue;
      }
      seen.add(key);
      const userId = this.extractConsumerUserId(user);
      options.push({ label: resolvedEmail, value: resolvedEmail, userId: userId || undefined });
    }
    return options;
  }

  /** Consumer id from basic user API — prefer `sub`. */
  private extractConsumerUserId(user: Record<string, unknown>): string {
    const sub = user['sub'];
    if (sub != null && String(sub).trim()) {
      return String(sub).trim();
    }
    return this.extractUserId({ result: user });
  }

  private extractUserResults(response: unknown): Record<string, unknown>[] {
    if (!response || typeof response !== 'object') {
      return [];
    }
    const root = response as Record<string, unknown>;
    if (Array.isArray(root['result'])) {
      return root['result'] as Record<string, unknown>[];
    }
    if (Array.isArray(root['data'])) {
      return root['data'] as Record<string, unknown>[];
    }
    const nested = root['result'] as Record<string, unknown> | undefined;
    if (nested) {
      if (Array.isArray(nested['results'])) {
        return nested['results'] as Record<string, unknown>[];
      }
      if (Array.isArray(nested['data'])) {
        return nested['data'] as Record<string, unknown>[];
      }
      if (Object.keys(nested).length > 0) {
        return [nested];
      }
    }
    return [];
  }

  private hasAnyUserResult(response: unknown): boolean {
    if (!response || typeof response !== 'object') return false;
    const r = response as Record<string, unknown>;
    const topLevelTotalCount = Number(r['totalCount'] ?? r['total_count'] ?? 0);
    if (Number.isFinite(topLevelTotalCount) && topLevelTotalCount > 0) {
      return true;
    }
    const result = r['result'];
    if (Array.isArray(result)) return result.length > 0;
    if (result && typeof result === 'object') {
      const resultObj = result as Record<string, unknown>;
      const nestedTotalCount = Number(resultObj['totalCount'] ?? resultObj['total_count'] ?? 0);
      if (Number.isFinite(nestedTotalCount) && nestedTotalCount > 0) {
        return true;
      }
      if (Array.isArray(resultObj['results'])) return resultObj['results'].length > 0;
      if (Array.isArray(resultObj['data'])) return resultObj['data'].length > 0;
      return Object.keys(resultObj).length > 0;
    }
    if (Array.isArray(r['data'])) return (r['data'] as unknown[]).length > 0;
    return false;
  }

  private clearIndividualUserLookupErrors(): void {
    const emailCtrl = this.form.get('individualEmail');
    if (emailCtrl) {
      const emailErrs = { ...(emailCtrl.errors ?? {}) };
      delete emailErrs['emailNotFound'];
      delete emailErrs['emailLookupFailed'];
      delete emailErrs['emailInvalidFormat'];
      emailCtrl.setErrors(Object.keys(emailErrs).length ? emailErrs : null);
    }
  }

  private setIndividualUserLookupError(
    key: 'emailNotFound' | 'emailLookupFailed' | 'emailInvalidFormat',
    value: true,
  ): void {
    const emailCtrl = this.form.get('individualEmail');
    if (!emailCtrl) {
      return;
    }
    const emailErrs = { ...(emailCtrl.errors ?? {}) };
    emailErrs[key] = value;
    emailCtrl.setErrors(emailErrs);
    emailCtrl.markAsTouched();
  }

  get selectedConstraintCount(): number {
    return this.visibleConstraintRows.filter((row) => this.form.get(row.enabledKey)?.value).length;
  }

  isConstraintRowEnabled(row: PolicyConstraintRow): boolean {
    return !!this.form.get(row.enabledKey)?.value;
  }

  goToStep(step: number): void {
    if (step < 1 || step > this.totalSteps || this.isStepLocked(step)) {
      return;
    }
    this.currentStep = step;
    this.persistWizardDraft();
    this.cdr.markForCheck();
  }

  isWizardStepActive(index: number): boolean {
    return this.currentStep === index + 1;
  }

  isWizardStepCompleted(index: number): boolean {
    return this.isStepCompleted(index + 1);
  }

  isWizardStepClickable(index: number): boolean {
    const step = index + 1;
    if (this.isStepLocked(step)) {
      return false;
    }
    if (step <= this.currentStep) {
      return true;
    }
    return this.isStepValid(this.currentStep);
  }

  isWizardConnectorCompleted(index: number): boolean {
    const nextIndex = index + 1;
    const currentIndex = this.currentStep - 1;
    return nextIndex < currentIndex || currentIndex === nextIndex;
  }

  onWizardStepClick(index: number): void {
    if (this.isWizardStepClickable(index)) {
      this.goToStep(index + 1);
    }
  }

  onWizardBackClick(): void {
    if (this.currentStep > 1) {
      this.previousStep();
      return;
    }
    if (this.embedded) {
      this.cancelled.emit();
      return;
    }
    const path = this.isOrgAdminRoute ? '/org-admin/access-requests' : '/provider/access-requests';
    void this.router.navigate([path]);
  }

  get wizardPrimaryLabel(): string {
    return this.currentStep === this.totalSteps ? 'Save policy' : 'Continue';
  }

  get isStepFourSubmitDisabled(): boolean {
    const provider = String(this.form.get('providerComment')?.value ?? '').trim();
    const feedback = String(this.form.get('feedbackComment')?.value ?? '').trim();
    return !provider || !feedback;
  }

  get isWizardPrimaryDisabled(): boolean {
    if (this.isSubmitting) {
      return true;
    }
    switch (this.currentStep) {
      case 1:
        return this.isStepOneContinueDisabled;
      case 2:
        return this.isStepTwoContinueDisabled;
      case 3:
        return this.isStepThreeContinueDisabled;
      case 4:
        return this.isStepFourSubmitDisabled;
      default:
        return false;
    }
  }

  onWizardPrimaryClick(event: Event): void {
    if (this.currentStep === this.totalSteps) {
      return;
    }
    event.preventDefault();
    this.nextStep();
  }

  nextStep(): void {
    if (!this.validateStep(this.currentStep)) {
      return;
    }
    if (this.currentStep < this.totalSteps) {
      this.currentStep += 1;
      this.persistWizardDraft();
      this.cdr.markForCheck();
    }
  }

  previousStep(): void {
    if (this.currentStep > 1) {
      this.currentStep -= 1;
      this.persistWizardDraft();
      this.cdr.markForCheck();
    }
  }

  isStepLocked(step: number): boolean {
    if (step <= 1) {
      return false;
    }
    for (let s = 1; s < step; s++) {
      if (!this.isStepCompleted(s)) {
        return true;
      }
    }
    return false;
  }

  /** Whether a step satisfies the same rules as Continue / submit (used for stepper state). */
  private isStepValid(step: number): boolean {
    switch (step) {
      case 1:
        return !this.isStepOneContinueDisabled;
      case 2:
        return !this.isStepTwoContinueDisabled;
      case 3:
        return !this.isStepThreeContinueDisabled;
      case 4:
        return !this.isStepFourSubmitDisabled;
      default:
        return true;
    }
  }

  isStepCompleted(step: number): boolean {
    if (step === 4) {
      return false;
    }
    return this.isStepValid(step);
  }

  getStepSummary(step: number): string {
    switch (step) {
      case 1: {
        if (this.isIndividualScope) {
          const email = String(this.form.get('individualEmail')?.value ?? '').trim();
          return email || 'Individual';
        }
        const orgCount = this.organisationSelection.length;
        const roleCount = this.roleSelection.length;
        if (!orgCount && !roleCount) return '';
        const parts: string[] = [];
        if (orgCount) parts.push(`${orgCount} org${orgCount > 1 ? 's' : ''}`);
        if (roleCount) parts.push(`${roleCount} role${roleCount > 1 ? 's' : ''}`);
        return parts.join(' · ');
      }
      case 2:
        if (!this.form.get('assetKind')?.value) return '';
        return this.assetSearchQuery || this.assetKindLabel;
      case 3:
        return this.selectedConstraintCount
          ? `${this.selectedConstraintCount} constraint${this.selectedConstraintCount > 1 ? 's' : ''}`
          : '';
      default:
        return '';
    }
  }

  private validateStep(step: number): boolean {
    switch (step) {
      case 1: {
        if (this.isGroupScope) {
          const organisationIds = this.form.get('organisationIds')?.value ?? [];
          if (!organisationIds.length) {
            this.toaster.error('Choose at least one organisation.');
            return false;
          }
          const targetRoles = this.form.get('targetRoles')?.value ?? [];
          if (!targetRoles.length) {
            this.toaster.error('Choose at least one role.');
            return false;
          }
          return true;
        }
        const email = String(this.form.get('individualEmail')?.value ?? '').trim();
        if (!email) {
          this.toaster.error('Enter an email address.');
          return false;
        }
        if (this.form.get('individualEmail')?.hasError('email')) {
          this.toaster.error('Enter a valid email address.');
          return false;
        }
        if (this.individualEmailLookupLoading) {
          this.toaster.error('Validating email. Please wait a moment.');
          return false;
        }
        if (this.lastVerifiedIndividualEmail !== email) {
          this.toaster.error('Validate the email before continuing.');
          return false;
        }
        if (this.form.get('individualEmail')?.hasError('emailNotFound') || this.individualEmailNotFound) {
          this.toaster.error('No user found for this email.');
          return false;
        }
        if (this.form.get('individualEmail')?.hasError('emailInvalidFormat') || this.individualEmailInvalidFormat) {
          this.toaster.error('Enter a valid email address.');
          return false;
        }
        if (this.form.get('individualEmail')?.hasError('emailLookupFailed')) {
          this.toaster.error('Unable to verify email right now. Please try again.');
          return false;
        }
        if (this.form.get('individualEmail')?.invalid) {
          this.toaster.error('Enter a valid email address.');
          return false;
        }
        return true;
      }
      case 2: {
        const assetKind = this.form.get('assetKind')?.value;
        if (!assetKind) {
          this.toaster.error('Choose an asset type.');
          return false;
        }
        const assetId = String(this.form.get('selectedAssetId')?.value ?? '').trim();
        if (!assetId) {
          this.toaster.error(`Choose a ${this.assetKindLabel.toLowerCase()}.`);
          return false;
        }
        return true;
      }
      case 3: {
        const hasAnyConstraintSelected = this.visibleConstraintRows.some(
          (row) => !!this.form.get(row.enabledKey)?.value,
        );
        if (!hasAnyConstraintSelected) {
          this.toaster.error('Select at least one constraint.');
          return false;
        }
        for (const row of this.visibleConstraintRows) {
          if (!this.form.get(row.enabledKey)?.value) continue;
          const dateStr = String(this.form.get(row.dateKey)?.value ?? '').trim();
          if (!dateStr) {
            this.toaster.error(`Provide expiry date for ${row.name}.`);
            return false;
          }
          if (this.isExpiryDateBeforeToday(dateStr)) {
            this.toaster.error(`Expiry date for ${row.name} cannot be in the past.`);
            return false;
          }
          const timeStr = String(this.form.get(row.timeKey)?.value ?? '').trim();
          if (!timeStr) {
            this.toaster.error(`Provide expiry time for ${row.name}.`);
            return false;
          }
          if (this.isExpiryDateTimeInPast(dateStr, timeStr)) {
            this.toaster.error(
              `Expiry time for ${row.name} must be later than the current time when selecting today's date.`,
            );
            return false;
          }
        }
        const accessPreview = this.buildAccessConstraints(this.form.getRawValue());
        if (!this.resolvePolicyExpiryIso(accessPreview)) {
          this.toaster.error('Could not compute policy expiry from constraints.');
          return false;
        }
        return true;
      }
      case 4: {
        const provider = String(this.form.get('providerComment')?.value ?? '').trim();
        if (!provider) {
          this.toaster.error('Enter notes.');
          return false;
        }
        const feedback = String(this.form.get('feedbackComment')?.value ?? '').trim();
        if (!feedback) {
          this.toaster.error('Enter requester comment.');
          return false;
        }
        return true;
      }
      default:
        return true;
    }
  }

  onOrganisationMultiselectChange(items: PolicyOrganisationOption[]): void {
    const selection = items.length > 1 ? items.slice(-1) : items;
    const ids = selection.map((item) => item.value);
    const current = this.form.get('organisationIds')?.value ?? [];
    if (this.sameStringArrays(ids, current)) {
      return;
    }
    this.organisationSelection = selection;
    this.form.patchValue({ organisationIds: ids });
    this.reorderOrganisationOptions(ids);
    this.cdr.markForCheck();
  }

  onRoleMultiselectChange(items: PolicyRoleOption[]): void {
    const roles = items.map((item) => item.value);
    const selection = this.toCanonicalRoleSelection(roles);
    const current = this.form.get('targetRoles')?.value ?? [];
    if (this.sameStringArrays(roles, current)) {
      return;
    }
    this.roleSelection = selection;
    this.form.patchValue({ targetRoles: roles });
    this.cdr.markForCheck();
  }

  /** Map role values to option labels from `roleOptions` (e.g. value `all` → label Consumer). */
  private toCanonicalRoleSelection(roles: PolicyTargetRole[]): PolicyRoleOption[] {
    return this.roleOptions.filter((option) => roles.includes(option.value));
  }

  onAssetKindMultiselectChange(items: PolicyAssetKindOption[]): void {
    const next = items[0]?.value ?? null;
    const current = this.form.get('assetKind')?.value ?? null;
    if (next === current) {
      return;
    }
    this.assetKindSelection = items;
    this.form.patchValue({ assetKind: next, selectedAssetId: '' });
    this.clearAssetSelection();
    this.cdr.markForCheck();
  }

  onAssetSearchInput(): void {
    if (!this.hasAssetKindSelected) {
      return;
    }

    if (this.searchDebounceTimer) {
      clearTimeout(this.searchDebounceTimer);
    }

    const query = this.assetSearchQuery.trim();
    if (!query) {
      this.assetSuggestions = [];
      this.showAssetAutocomplete = false;
      this.assetSearchAttempted = false;
      this.form.patchValue({ selectedAssetId: '' }, { emitEvent: false });
      this.persistWizardDraft();
      this.cdr.markForCheck();
      return;
    }

    this.searchDebounceTimer = setTimeout(() => {
      this.persistWizardDraft();
      void this.searchAssets(query);
    }, 300);
  }

  onAssetSearchFocus(): void {
    this.closeAllFormDropdowns();
    const query = this.assetSearchQuery.trim();
    if (query) {
      void this.searchAssets(query);
    }
  }

  onMultiselectTriggerMousedown(emittingMultiselect: MultiselectComponent): void {
    this.closeAssetAutocomplete();
    this.multiselectComponents?.forEach((ms) => {
      if (ms !== emittingMultiselect && ms.isOpen) {
        ms.closeDropdown();
      }
    });
    this.closeAllDateRangePickers();
    this.closeAllTimePickers();
  }

  onMultiselectOpen(): void {
    this.closeAssetAutocomplete();
    const currentlyOpening = this.multiselectComponents?.find((ms) => ms.isOpen);
    this.multiselectComponents?.forEach((ms) => {
      if (ms !== currentlyOpening && ms.isOpen) {
        ms.closeDropdown();
      }
    });
    this.closeAllDateRangePickers();
    this.closeAllTimePickers();
  }

  onDateRangePickerOpen(): void {
    this.closeAssetAutocomplete();
    this.closeAllMultiselects();
    const currentlyOpening = this.dateRangePickerComponents?.find((picker) => picker.isOpen);
    this.dateRangePickerComponents?.forEach((picker) => {
      if (picker !== currentlyOpening && picker.isOpen) {
        picker.closeDropdown();
      }
    });
    this.closeAllTimePickers();
  }

  onTimePickerOpen(): void {
    this.closeAssetAutocomplete();
    this.closeAllMultiselects();
    this.closeAllDateRangePickers();
    const currentlyOpening = this.timePickerComponents?.find((picker) => picker.isOpen);
    this.timePickerComponents?.forEach((picker) => {
      if (picker !== currentlyOpening && picker.isOpen) {
        picker.closeDropdown();
      }
    });
    currentlyOpening?.refreshMinTimeConstraint();
    this.cdr.markForCheck();
  }

  onConstraintTimePickerOpen(row: PolicyConstraintRow): void {
    if (this.isConstraintRowTimeDisabled(row)) {
      const idx = this.constraintRows.findIndex((r) => r.timeKey === row.timeKey);
      const picker = this.timePickerComponents?.toArray()[idx];
      picker?.closeDropdown();
      return;
    }
    this.onTimePickerOpen();
  }

  private closeAllFormDropdowns(): void {
    this.closeAssetAutocomplete();
    this.closeAllMultiselects();
    this.closeAllDateRangePickers();
    this.closeAllTimePickers();
  }

  private closeAssetAutocomplete(): void {
    if (this.showAssetAutocomplete) {
      this.showAssetAutocomplete = false;
      this.cdr.markForCheck();
    }
  }

  private closeAllMultiselects(): void {
    this.multiselectComponents?.forEach((ms) => {
      if (ms.isOpen) {
        ms.closeDropdown();
      }
    });
  }

  private closeAllDateRangePickers(): void {
    this.dateRangePickerComponents?.forEach((picker) => {
      if (picker.isOpen) {
        picker.closeDropdown();
      }
    });
  }

  private closeAllTimePickers(): void {
    this.timePickerComponents?.forEach((picker) => {
      if (picker.isOpen) {
        picker.closeDropdown();
      }
    });
  }

  selectAssetSuggestion(option: PolicyAssetOption): void {
    this.form.patchValue({ selectedAssetId: option.id });
    this.assetSearchQuery = option.label;
    this.selectedAssetAccessTypes = option.accessTypes ?? [];
    this.syncConstraintAvailability();
    this.showAssetAutocomplete = false;
    this.assetSuggestions = [];
    this.assetSearchAttempted = false;
    this.persistWizardDraft();
    this.cdr.markForCheck();
  }

  clearAssetSelection(): void {
    this.assetSearchQuery = '';
    this.assetSuggestions = [];
    this.selectedAssetAccessTypes = [];
    this.syncConstraintAvailability();
    this.showAssetAutocomplete = false;
    this.assetSearchAttempted = false;
    this.form.patchValue({ selectedAssetId: '' }, { emitEvent: false });
    this.persistWizardDraft();
  }

  onRowExpiryDateChange(row: PolicyConstraintRow, range: DateRange | null): void {
    this.setExpiryDateControl(row.dateKey, range);
    this.syncRowExpiryDateRangeModel(row.dateKey);
    const dateStr = String(this.form.get(row.dateKey)?.value ?? '').trim();
    const timeStr = String(this.form.get(row.timeKey)?.value ?? '').trim();
    if (!dateStr && timeStr) {
      this.form.patchValue({ [row.timeKey]: '' }, { emitEvent: false });
    } else if (dateStr && timeStr && this.isExpiryDateTimeInPast(dateStr, timeStr)) {
      // If selected date is today and time is in the past, clear it to force re-selection.
      this.form.patchValue({ [row.timeKey]: '' }, { emitEvent: false });
    }
    this.syncConstraintExpiryFieldStates();
    this.timePickerComponents?.forEach((picker) => picker.refreshMinTimeConstraint());
    this.cdr.markForCheck();
  }

  /** Earliest selectable time when expiry date is today (aligned to 5-minute picker steps). */
  getConstraintRowMinTime(row: PolicyConstraintRow): string {
    const dateStr = String(this.form.get(row.dateKey)?.value ?? '').trim();
    if (!this.isExpiryDateToday(dateStr)) {
      return '';
    }
    return this.getTodayMinExpiryTimeHHmm();
  }

  private getTodayMinExpiryTimeHHmm(): string {
    const step = 5;
    const now = new Date();
    let totalMinutes = now.getHours() * 60 + now.getMinutes();
    totalMinutes = Math.ceil(totalMinutes / step) * step;
    if (totalMinutes >= 24 * 60) {
      totalMinutes = 23 * 60 + 55;
    }
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
  }

  onConstraintRowClick(event: Event, row: PolicyConstraintRow): void {
    const target = event.target as HTMLElement;
    if (
      target.closest('input[type="checkbox"]') ||
      target.closest('.add-policy-home__td--date') ||
      target.closest('.add-policy-home__td--time') ||
      target.closest('app-date-range-picker') ||
      target.closest('app-time-picker')
    ) {
      return;
    }
    const control = this.form.get(row.enabledKey);
    if (!control) {
      return;
    }
    const next = !control.value;
    control.setValue(next);
    if (!next) {
      this.clearConstraintExpiryFields(row);
    }
    this.syncConstraintExpiryFieldStates();
    this.cdr.markForCheck();
  }

  onConstraintToggle(row: PolicyConstraintRow, event: Event): void {
    const checked = (event.target as HTMLInputElement | null)?.checked ?? false;
    if (!checked) {
      this.clearConstraintExpiryFields(row);
    }
    this.syncConstraintExpiryFieldStates();
  }

  /** Clears form + date-picker model when a constraint is unchecked. */
  private clearConstraintExpiryFields(row: PolicyConstraintRow): void {
    this.form.patchValue(
      {
        [row.dateKey]: '',
        [row.timeKey]: '',
      },
      { emitEvent: false },
    );
    // New object reference so ngModel/CVA runs writeValue (in-place mutation does not).
    this.rowExpiryDateRangeModels[row.dateKey] = { startDate: null, endDate: null };
    this.resetDatePickerDisplayForRow(row);
    this.cdr.markForCheck();
  }

  private resetDatePickerDisplayForRow(row: PolicyConstraintRow): void {
    const idx = this.constraintRows.findIndex((r) => r.dateKey === row.dateKey);
    if (idx < 0) {
      return;
    }
    const picker = this.dateRangePickerComponents?.toArray()[idx];
    picker?.writeValue({ startDate: null, endDate: null });
  }

  isConstraintRowExpiryDisabled(row: PolicyConstraintRow): boolean {
    return !this.form.get(row.enabledKey)?.value;
  }

  isConstraintRowTimeDisabled(row: PolicyConstraintRow): boolean {
    if (!this.form.get(row.enabledKey)?.value) {
      return true;
    }
    return !String(this.form.get(row.dateKey)?.value ?? '').trim();
  }

  /** Time uses formControlName — must disable the control (not only [disabled] input). */
  private syncConstraintExpiryFieldStates(): void {
    for (const row of this.constraintRows) {
      const enabled = !!this.form.get(row.enabledKey)?.value;
      const hasDate = !!String(this.form.get(row.dateKey)?.value ?? '').trim();
      const timeCtrl = this.form.get(row.timeKey);
      if (!timeCtrl) {
        continue;
      }
      const timeAllowed = enabled && hasDate;
      const timeStr = String(timeCtrl.value ?? '').trim();
      if (!timeAllowed && timeStr) {
        timeCtrl.setValue('', { emitEvent: false });
      }
      if (timeAllowed) {
        if (timeCtrl.disabled) {
          timeCtrl.enable({ emitEvent: false });
        }
      } else if (timeCtrl.enabled) {
        timeCtrl.disable({ emitEvent: false });
      }
    }

    this.timePickerComponents?.forEach((picker) => {
      if (picker.disabled && picker.isOpen) {
        picker.closeDropdown();
      }
    });
  }

  private syncAssetKindSelection(): void {
    const kind = this.form.get('assetKind')?.value ?? null;
    if (!kind) {
      if (this.assetKindSelection.length > 0) {
        this.assetKindSelection = [];
      }
      return;
    }
    const opt = this.assetKindOptions.find((o) => o.value === kind);
    if (opt && this.assetKindSelection[0]?.value !== opt.value) {
      this.assetKindSelection = [opt];
    }
  }

  private syncRowExpiryDateRangeModel(dateKey: PolicyConstraintRow['dateKey']): void {
    this.applyDateStringToModel(
      this.rowExpiryDateRangeModels[dateKey],
      String(this.form.get(dateKey)?.value ?? ''),
    );
  }

  private syncAllRowExpiryDateRangeModels(): void {
    for (const row of this.constraintRows) {
      this.syncRowExpiryDateRangeModel(row.dateKey);
    }
  }

  private applyDateStringToModel(model: DateRange, value: string): void {
    const next = this.toDateRange(value);
    const prevTime = model.startDate?.getTime() ?? null;
    const nextTime = next.startDate?.getTime() ?? null;
    if (prevTime === nextTime) {
      return;
    }
    model.startDate = next.startDate;
    model.endDate = next.endDate;
  }

  private toDateRange(value: string): DateRange {
    const trimmed = String(value ?? '').trim();
    if (!trimmed) {
      return { startDate: null, endDate: null };
    }
    const parsed = new Date(`${trimmed}T00:00:00`);
    if (Number.isNaN(parsed.getTime())) {
      return { startDate: null, endDate: null };
    }
    return { startDate: parsed, endDate: null };
  }

  private setExpiryDateControl(
    controlName: PolicyConstraintRow['dateKey'],
    range: DateRange | null,
  ): void {
    const next = range?.startDate ? this.formatDateForForm(range.startDate) : '';
    const current = String(this.form.get(controlName)?.value ?? '');
    if (current === next) {
      return;
    }
    this.form.patchValue({ [controlName]: next }, { emitEvent: false });
  }

  private isExpiryDateBeforeToday(dateStr: string): boolean {
    const parsed = new Date(`${dateStr}T00:00:00`);
    if (Number.isNaN(parsed.getTime())) {
      return false;
    }
    parsed.setHours(0, 0, 0, 0);
    const today = new Date(this.constraintExpiryMinDate);
    return parsed < today;
  }

  private isExpiryDateTimeInPast(dateStr: string, timeStr: string): boolean {
    const trimmedDate = String(dateStr ?? '').trim();
    const trimmedTime = String(timeStr ?? '').trim();
    if (!trimmedDate || !trimmedTime) {
      return false;
    }

    // Apply this validation only when expiry date is today.
    if (!this.isExpiryDateToday(trimmedDate)) {
      return false;
    }

    const normalizedTime = trimmedTime.length === 5 ? `${trimmedTime}:00` : trimmedTime;
    const parsedDateTime = new Date(`${trimmedDate}T${normalizedTime}`);
    if (Number.isNaN(parsedDateTime.getTime())) {
      return false;
    }
    return parsedDateTime.getTime() <= Date.now();
  }

  private isExpiryDateToday(dateStr: string): boolean {
    const parsed = new Date(`${dateStr}T00:00:00`);
    if (Number.isNaN(parsed.getTime())) {
      return false;
    }
    parsed.setHours(0, 0, 0, 0);
    const today = new Date(this.constraintExpiryMinDate);
    return parsed.getTime() === today.getTime();
  }

  private formatDateForForm(date: Date): string {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  }

  private async loadOrganisations(): Promise<void> {
    this.organisationsLoading = true;
    this.cdr.markForCheck();
    try {
      const response = await this.authApi.getAllOrganisations(1, 500);
      let list: Record<string, unknown>[] = [];
      if (response && Array.isArray((response as Record<string, unknown>)['result'])) {
        list = (response as Record<string, unknown>)['result'] as Record<string, unknown>[];
      } else if (Array.isArray(response)) {
        list = response as Record<string, unknown>[];
      }
      const mapped = list
        .map((org) => ({
          value: String(org['id'] ?? org['organisationId'] ?? ''),
          label: String(org['orgName'] ?? org['name'] ?? '').trim(),
        }))
        .filter((org) => org.value && org.label);
      const unique = new Map<string, PolicyOrganisationOption>();
      for (const org of mapped) {
        unique.set(org.value, org);
      }
      this.organisationOptions = Array.from(unique.values()).sort((a, b) =>
        a.label.localeCompare(b.label),
      );
      await this.applyDefaultOrganisationSelection();
      if (this.wizardDraftRestored) {
        this.reconcileRestoredOrganisationSelection();
      }
    } catch {
      this.organisationOptions = [];
      this.toaster.error('Failed to load organisations.');
    } finally {
      this.organisationsLoading = false;
      this.cdr.markForCheck();
    }
  }

  private clearOrganisationSelection(): void {
    this.organisationSelection = [];
    this.form.patchValue({ organisationIds: [] }, { emitEvent: false });
    this.reorderOrganisationOptions([]);
  }

  private async applyDefaultOrganisationSelection(): Promise<void> {
    if (this.isIndividualScope) {
      return;
    }

    const current = this.form.get('organisationIds')?.value ?? [];
    if (current.length > 0) {
      return;
    }

    const userOrgId = await this.resolveUserOrganisationId();
    if (!userOrgId) {
      return;
    }

    let option = this.organisationOptions.find((org) => org.value === userOrgId);
    if (!option) {
      const label = await this.resolveUserOrganisationName();
      if (!label) {
        return;
      }
      option = { value: userOrgId, label };
      this.organisationOptions = [...this.organisationOptions, option].sort((a, b) =>
        a.label.localeCompare(b.label),
      );
    }

    this.organisationSelection = [option];
    this.form.patchValue({ organisationIds: [option.value] });
    this.reorderOrganisationOptions([option.value]);
    this.cdr.markForCheck();
  }

  /**
   * Keep selected orgs pinned at the top of dropdown while preserving
   * alphabetical ordering within selected and unselected groups.
   */
  private reorderOrganisationOptions(selectedIds: string[]): void {
    const selectedSet = new Set(selectedIds);
    const selected = this.organisationOptions
      .filter((org) => selectedSet.has(org.value))
      .sort((a, b) => a.label.localeCompare(b.label));
    const unselected = this.organisationOptions
      .filter((org) => !selectedSet.has(org.value))
      .sort((a, b) => a.label.localeCompare(b.label));
    this.organisationOptions = [...selected, ...unselected];
  }

  private async resolveUserOrganisationId(): Promise<string> {
    try {
      const cached = localStorage.getItem('userData');
      if (cached) {
        const fromCache = this.extractOrganisationId(JSON.parse(cached) as Record<string, unknown>);
        if (fromCache) {
          return fromCache;
        }
      }
      const userInfo = await this.authApi.getUserInfo();
      return this.extractOrganisationId(userInfo as Record<string, unknown>);
    } catch {
      return '';
    }
  }

  private async resolveUserOrganisationName(): Promise<string> {
    try {
      const cached = localStorage.getItem('userData');
      if (cached) {
        const fromCache = this.extractOrganisationName(JSON.parse(cached) as Record<string, unknown>);
        if (fromCache) {
          return fromCache;
        }
      }
      const userInfo = await this.authApi.getUserInfo();
      return this.extractOrganisationName(userInfo as Record<string, unknown>);
    } catch {
      return '';
    }
  }

  private extractOrganisationId(data: Record<string, unknown>): string {
    const result = (data['result'] as Record<string, unknown> | undefined) ?? data;
    let id = String(
      result['organisationId'] ??
        result['organisation_id'] ??
        result['orgId'] ??
        result['org_id'] ??
        '',
    ).trim();

    const organisation = result['organisation'] as Record<string, unknown> | undefined;
    if (!id && organisation) {
      id = String(
        organisation['organisationId'] ??
          organisation['organisation_id'] ??
          organisation['id'] ??
          organisation['orgId'] ??
          '',
      ).trim();
    }
    return id;
  }

  private extractOrganisationName(data: Record<string, unknown>): string {
    const result = (data['result'] as Record<string, unknown> | undefined) ?? data;
    const organisation = result['organisation'] as Record<string, unknown> | undefined;
    return String(
      organisation?.['orgName'] ??
        organisation?.['name'] ??
        organisation?.['organisationName'] ??
        result['orgName'] ??
        result['organisationName'] ??
        '',
    ).trim();
  }

  private sameStringArrays(a: string[], b: string[]): boolean {
    if (a.length !== b.length) {
      return false;
    }
    const sortedA = [...a].sort();
    const sortedB = [...b].sort();
    return sortedA.every((val, idx) => val === sortedB[idx]);
  }

  private async searchAssets(query: string): Promise<void> {
    if (!query.trim()) {
      this.assetSuggestions = [];
      this.showAssetAutocomplete = false;
      this.assetSearchAttempted = false;
      return;
    }

    const kind = this.form.get('assetKind')?.value;
    if (!kind) {
      return;
    }

    this.assetsSearching = true;
    this.showAssetAutocomplete = false;
    this.assetSuggestions = [];
    this.cdr.markForCheck();

    try {
      const items = this.isProviderRoute
        ? await this.searchProviderAssetsForPolicy(kind, query)
        : await this.searchOrganisationAssetsForPolicy(kind, query);
      this.assetSuggestions = items.map((item) => ({
        id: String(item['id'] ?? item['assetId'] ?? ''),
        label: String(item['label'] ?? item['name'] ?? item['title'] ?? 'Untitled'),
        organization: String(
          item['provider'] ?? item['organisation'] ?? item['organization'] ?? item['provider_name'] ?? '',
        ),
        accessTypes: this.extractAssetAccessTypesFromItem(item),
      }));
      this.assetSearchAttempted = true;
      this.showAssetAutocomplete = this.assetSuggestions.length > 0;
    } catch {
      this.assetSuggestions = [];
      this.showAssetAutocomplete = false;
      this.assetSearchAttempted = false;
      this.toaster.error(`Failed to search ${kind === 'model' ? 'models' : 'datasets'}.`);
    } finally {
      this.assetsSearching = false;
      this.cdr.markForCheck();
    }
  }

  /** Provider portal: same API as Manage Assets (`POST …/search/myassets`). */
  private async searchProviderAssetsForPolicy(
    kind: PolicyAssetKind,
    query: string,
  ): Promise<Record<string, unknown>[]> {
    const searchCriteria = this.buildPolicyAssetSearchCriteria(kind);
    const searchTrimmed = query.trim();
    const filterPayload =
      searchTrimmed !== ''
        ? {
            q: searchTrimmed,
            autoComplete: true,
            fuzzy: true,
            searchCriteria,
          }
        : { searchCriteria };

    const res = await this.catalogueApi.getMyAssets(filterPayload, 1, 15, 'name:asc');
    return this.filterAssetsForPolicy(this.extractItemsFromSearchResponse(res), kind).slice(0, 15);
  }

  private async searchOrganisationAssetsForPolicy(
    kind: PolicyAssetKind,
    query: string,
  ): Promise<Record<string, unknown>[]> {
    const searchCriteria = this.buildPolicyAssetSearchCriteria(kind);
    const res = await this.catalogueApi.getOrganisationAsset(1, 100, 'name:asc', searchCriteria);
    const allItems = this.filterAssetsForPolicy(this.extractItemsFromSearchResponse(res), kind);
    const queryNormalized = query.trim().toLowerCase();
    if (!queryNormalized) {
      return allItems.slice(0, 15);
    }
    return allItems
      .filter((item) => {
        const label = String(item['label'] ?? item['name'] ?? item['title'] ?? '').toLowerCase();
        return label.includes(queryNormalized);
      })
      .slice(0, 15);
  }

  /** Step 2: only Restricted + ACTIVE (published) assets with completed upload are eligible. */
  private buildPolicyAssetSearchCriteria(kind: PolicyAssetKind): SearchCriteria[] {
    return [
      {
        searchType: 'term',
        field: 'type',
        values: kind === 'dataset' ? ['adex:DataBank'] : ['adex:aiModel'],
      },
      {
        searchType: 'term',
        field: 'publishStatus',
        values: ['ACTIVE'],
      },
      {
        searchType: 'term',
        field: 'accessPolicy',
        values: ['restricted'],
      },
      {
        searchType: 'term',
        field: 'dataUploadStatus',
        values: [true],
      },
    ];
  }

  private filterAssetsForPolicy(
    items: Record<string, unknown>[],
    _kind: PolicyAssetKind,
  ): Record<string, unknown>[] {
    return items.filter((item) => this.isRestrictedAccessPolicy(item));
  }

  private isRestrictedAccessPolicy(item: Record<string, unknown>): boolean {
    const raw = String(
      item['accessPolicy'] ?? item['access_policy'] ?? item['permission'] ?? '',
    ).trim();
    return raw.toUpperCase() === 'RESTRICTED';
  }

  private normalizeResourceServerType(value: string): string {
    return String(value ?? '')
      .trim()
      .toLowerCase()
      .replace(/_/g, '-');
  }

  /**
   * Constraint options come from catalogue `resourceServer[].accessTypes` (`api` | `file`).
   * Falls back to type/name when accessTypes is missing (older assets).
   */
  private extractAssetAccessTypesFromItem(item: Record<string, unknown>): string[] {
    const raw = item['resourceServer'] ?? item['resource_server'];
    if (!raw) {
      return [];
    }
    const servers = Array.isArray(raw) ? raw : [raw];
    const accessTypes = new Set<string>();
    for (const entry of servers) {
      if (!entry || typeof entry !== 'object') {
        continue;
      }
      const server = entry as Record<string, unknown>;
      const fromApi = server['accessTypes'];
      if (Array.isArray(fromApi) && fromApi.length > 0) {
        for (const value of fromApi) {
          const normalized = String(value ?? '')
            .trim()
            .toLowerCase();
          if (normalized === 'api' || normalized === 'file') {
            accessTypes.add(normalized);
          }
        }
        continue;
      }
      // ponytail: older assets may omit accessTypes; map type/name → api|file
      const type = this.normalizeResourceServerType(String(server['type'] ?? ''));
      const name = String(server['name'] ?? '')
        .trim()
        .toUpperCase();
      if (type === 'file' || name === 'FILE' || name.includes('FILE')) {
        accessTypes.add('file');
      }
      if (
        type === 'ngsi-ld' ||
        type === 'ngsild' ||
        type === 'gateway' ||
        type === 'ogc' ||
        name.includes('NGSI')
      ) {
        accessTypes.add('api');
      }
    }
    return Array.from(accessTypes);
  }

  private syncConstraintAvailability(): void {
    const visibleKeys = new Set(this.visibleConstraintRows.map((row) => row.enabledKey));
    const patch: Record<string, string | boolean> = {};
    for (const row of this.constraintRows) {
      if (visibleKeys.has(row.enabledKey)) {
        continue;
      }
      patch[row.enabledKey] = false;
      patch[row.dateKey] = '';
      patch[row.timeKey] = '';
    }
    if (Object.keys(patch).length) {
      this.form.patchValue(patch, { emitEvent: false });
      this.syncAllRowExpiryDateRangeModels();
      this.syncConstraintExpiryFieldStates();
    }
  }

  private validateForm(): boolean {
    for (let step = 1; step <= this.totalSteps; step++) {
      if (!this.validateStep(step)) {
        return false;
      }
    }
    return true;
  }

  async onSubmit(): Promise<void> {
    if (!this.validateForm() || this.isSubmitting) {
      return;
    }

    this.isSubmitting = true;
    this.cdr.markForCheck();

    try {
      const isIndividual = this.form.get('scopeType')?.value === 'individual';
      const userId = isIndividual ? this.resolvePolicyUserId() : '';
      if (isIndividual && !userId) {
        this.toaster.error('Unable to resolve selected user. Please validate the user again.');
        return;
      }

      const apiPayload = this.buildCreatePolicyPayload(userId);
      await this.aclApdApi.createPolicy(apiPayload);
      this.toaster.success('Policy saved successfully.');
      await this.resetWizardForNextPolicy();
      if (this.embedded) {
        this.policySaved.emit();
      }
    } catch (error: unknown) {
      this.handleCreatePolicyError(error);
    } finally {
      this.isSubmitting = false;
      this.cdr.markForCheck();
    }
  }

  private buildCreatePolicyPayload(userId: string): PolicyCreatePayload {
    const v = this.form.getRawValue();
    const accessEntries = this.buildAccessConstraints(v);
    const expiryTime = this.resolvePolicyExpiryIso(accessEntries);
    const assetKind = v.assetKind;
    if (!assetKind) {
      throw new Error('Asset kind is required');
    }

    const isIndividual = v.scopeType === 'individual';
    const requestItem: PolicyCreateRequestItem = {
      policyType: isIndividual ? 'INDIVIDUAL' : 'GROUP',
      itemId: String(v.selectedAssetId ?? '').trim(),
      itemType: assetKind === 'model' ? 'AIMODEL' : 'DATABANK',
      apdURL: resolveCatalogueApdUrl(environment.basePathUrl),
      expiryTime,
      constraints: isIndividual
        ? { access: accessEntries }
        : { access: accessEntries, subjects: this.buildPolicySubjects(v) },
      providerComment: v.providerComment?.trim() ?? '',
      feedbackToConsumer: v.feedbackComment?.trim() ?? '',
    };

    if (isIndividual) {
      requestItem.userId = userId;
    }

    return { request: [requestItem] };
  }

  private buildPolicySubjects(
    v: ReturnType<typeof this.form.getRawValue>,
  ): NonNullable<PolicyCreateRequestItem['constraints']['subjects']> {
    return {
      allowedOrgIds: v.organisationIds ?? [],
      allowedRoles: this.resolveAllowedRoles(v.targetRoles ?? []),
    };
  }

  private buildAccessConstraints(
    v: ReturnType<typeof this.form.getRawValue>,
  ): PolicyCreateRequestItem['constraints']['access'] {
    const access: PolicyCreateRequestItem['constraints']['access'] = [];

    for (const row of this.visibleConstraintRows) {
      if (!v[row.enabledKey]) {
        continue;
      }
      const dateStr = String(v[row.dateKey] ?? '').trim();
      const timeStr = String(v[row.timeKey] ?? '').trim();
      const expiry = istToUtc(dateStr, timeStr);
      if (!expiry) {
        continue;
      }
      access.push({
        accessType: row.accessType,
        expiry,
      });
    }
    return access;
  }

  private resolvePolicyExpiryIso(
    accessEntries: PolicyCreateRequestItem['constraints']['access'],
  ): string {
    let maxMillis = 0;
    let latestExpiry = '';
    for (const entry of accessEntries) {
      const millis = parsePolicyExpiryIsoToMillis(entry.expiry);
      if (millis > maxMillis) {
        maxMillis = millis;
        latestExpiry = entry.expiry;
      }
    }
    return latestExpiry;
  }

  private resolveAllowedRoles(roles: PolicyTargetRole[]): string[] {
    const unique = [...new Set(roles.filter(Boolean))];
    if (!unique.length) {
      return [];
    }

    // Consumer-only still uses [] (API convention for unrestricted consumer access).
    const consumerOnly = unique.length === 1 && unique[0] === 'all';
    if (consumerOnly) {
      return [];
    }

    const apiRoles = new Set<string>();
    for (const role of unique) {
      apiRoles.add(this.mapRoleToApi(role));
    }
    return Array.from(apiRoles);
  }

  private mapRoleToApi(role: PolicyTargetRole): string {
    if (role === 'org-admin') {
      return 'org_admin';
    }
    if (role === 'all') {
      return 'consumer';
    }
    return role;
  }

  private resolvePolicyUserId(): string {
    const email = String(this.form.get('individualEmail')?.value ?? '').trim();
    if (email) {
      const matchedOption = this.individualEmailOptions.find((option) => option.value === email);
      if (matchedOption?.userId) {
        return matchedOption.userId;
      }
    }
    return this.selectedIndividualUserId.trim();
  }

  private extractUserId(data: Record<string, unknown>): string {
    const result = (data['result'] as Record<string, unknown> | undefined) ?? data;
    const candidates = [
      result['userId'],
      result['user_id'],
      result['sub'],
      data['userId'],
      data['user_id'],
      data['sub'],
      result['id'],
      data['id'],
    ];
    for (const c of candidates) {
      if (c != null && String(c).trim()) {
        return String(c).trim();
      }
    }
    return '';
  }

  private handleCreatePolicyError(error: unknown): void {
    const err = error as { status?: number; error?: { type?: string; detail?: string }; message?: string };
    if (err?.status === 409 || err?.error?.type === 'urn:dx:acl:conflict') {
      this.toaster.error('Policy already exists for this asset.');
      return;
    }
    const detail = err?.error?.detail ?? err?.message;
    this.toaster.error(detail ? String(detail) : 'Failed to save policy. Please try again.');
  }

  private extractItemsFromSearchResponse(response: unknown): Record<string, unknown>[] {
    if (!response || typeof response !== 'object') {
      return [];
    }
    const r = response as Record<string, unknown>;
    if (Array.isArray(r['result'])) {
      return r['result'] as Record<string, unknown>[];
    }
    const nested = r['result'] as Record<string, unknown> | undefined;
    if (nested && Array.isArray(nested['results'])) {
      return nested['results'] as Record<string, unknown>[];
    }
    if (Array.isArray(r['data'])) {
      return r['data'] as Record<string, unknown>[];
    }
    if (Array.isArray(r['items'])) {
      return r['items'] as Record<string, unknown>[];
    }
    return [];
  }

  private get wizardDraftStorageKey(): string {
    return `mahaagx-add-policy-wizard-draft:${this.isOrgAdminRoute ? 'org-admin' : 'provider'}`;
  }

  private get reloadPreserveDraftKey(): string {
    return `mahaagx-add-policy-reload-preserve:${this.isOrgAdminRoute ? 'org-admin' : 'provider'}`;
  }

  private markDraftForReloadPreserve(): void {
    if (typeof sessionStorage === 'undefined') {
      return;
    }
    try {
      sessionStorage.setItem(this.reloadPreserveDraftKey, '1');
    } catch {
      // Ignore quota / private-mode errors
    }
  }

  private shouldPreserveDraftForReload(): boolean {
    if (typeof sessionStorage === 'undefined') {
      return false;
    }
    return sessionStorage.getItem(this.reloadPreserveDraftKey) === '1';
  }

  private consumeReloadPreserveDraftFlag(): boolean {
    if (typeof sessionStorage === 'undefined') {
      return false;
    }
    const preserve = sessionStorage.getItem(this.reloadPreserveDraftKey) === '1';
    try {
      sessionStorage.removeItem(this.reloadPreserveDraftKey);
    } catch {
      // Ignore private-mode errors
    }
    return preserve;
  }

  private persistWizardDraft(): void {
    if (typeof sessionStorage === 'undefined') {
      return;
    }
    try {
      const v = this.form.getRawValue();
      const draft: AddPolicyWizardDraft = {
        currentStep: this.currentStep,
        form: {
          scopeType: v.scopeType ?? 'group',
          organisationIds: Array.isArray(v.organisationIds) ? v.organisationIds : [],
          targetRoles: Array.isArray(v.targetRoles) ? v.targetRoles : [],
          individualName: v.individualName ?? '',
          individualEmail: v.individualEmail ?? '',
          assetKind: v.assetKind ?? null,
          selectedAssetId: v.selectedAssetId ?? '',
          constraintApi: !!v.constraintApi,
          constraintFile: !!v.constraintFile,
          apiExpiryDate: v.apiExpiryDate ?? '',
          apiExpiryTime: v.apiExpiryTime ?? '',
          fileExpiryDate: v.fileExpiryDate ?? '',
          fileExpiryTime: v.fileExpiryTime ?? '',
          providerComment: v.providerComment ?? '',
          feedbackComment: v.feedbackComment ?? '',
        },
        organisationSelection: [...this.organisationSelection],
        roleSelection: [...this.roleSelection],
        assetSearchQuery: this.assetSearchQuery,
        selectedAssetAccessTypes: [...this.selectedAssetAccessTypes],
        lastVerifiedIndividualEmail: this.lastVerifiedIndividualEmail,
        individualEmailOptions: [...this.individualEmailOptions],
      };
      sessionStorage.setItem(this.wizardDraftStorageKey, JSON.stringify(draft));
    } catch {
      // Ignore quota / private-mode errors
    }
  }

  private restoreWizardDraft(): boolean {
    if (typeof sessionStorage === 'undefined') {
      return false;
    }
    try {
      const raw = sessionStorage.getItem(this.wizardDraftStorageKey);
      if (!raw) {
        return false;
      }
      const draft = JSON.parse(raw) as AddPolicyWizardDraft;
      if (!draft?.form) {
        return false;
      }

      this.form.patchValue(
        {
          scopeType: draft.form.scopeType ?? 'group',
          organisationIds: Array.isArray(draft.form.organisationIds) ? draft.form.organisationIds : [],
          targetRoles: Array.isArray(draft.form.targetRoles) ? draft.form.targetRoles : [],
          individualName: draft.form.individualName ?? '',
          individualEmail: draft.form.individualEmail ?? '',
          assetKind: draft.form.assetKind ?? null,
          selectedAssetId: draft.form.selectedAssetId ?? '',
          constraintApi: !!draft.form.constraintApi,
          constraintFile: !!draft.form.constraintFile,
          apiExpiryDate: draft.form.apiExpiryDate ?? '',
          apiExpiryTime: draft.form.apiExpiryTime ?? '',
          fileExpiryDate: draft.form.fileExpiryDate ?? '',
          fileExpiryTime: draft.form.fileExpiryTime ?? '',
          providerComment: draft.form.providerComment ?? '',
          feedbackComment: draft.form.feedbackComment ?? '',
        },
        { emitEvent: false },
      );

      this.organisationSelection = Array.isArray(draft.organisationSelection)
        ? [...draft.organisationSelection]
        : [];
      this.roleSelection = Array.isArray(draft.roleSelection) ? [...draft.roleSelection] : [];
      this.assetSearchQuery = draft.assetSearchQuery ?? '';
      this.selectedAssetAccessTypes = Array.isArray(draft.selectedAssetAccessTypes)
        ? [...draft.selectedAssetAccessTypes]
        : Array.isArray(draft.selectedAssetResourceServerTypes)
          ? [...draft.selectedAssetResourceServerTypes]
          : [];
      this.lastVerifiedIndividualEmail = draft.lastVerifiedIndividualEmail ?? '';
      this.individualEmailOptions = Array.isArray(draft.individualEmailOptions)
        ? [...draft.individualEmailOptions]
        : [];
      const restoredEmail = String(draft.form.individualEmail ?? '').trim();
      const restoredOption = this.individualEmailOptions.find((option) => option.value === restoredEmail);
      this.selectedIndividualUserId = restoredOption?.userId ?? '';
      this.currentStep = this.resolveRestoredWizardStep(draft.currentStep ?? 1);
      this.updateIndividualEmailFieldState();
      this.wizardDraftRestored = true;
      return true;
    } catch {
      return false;
    }
  }

  private resolveRestoredWizardStep(requestedStep: number): number {
    let step = Math.min(this.totalSteps, Math.max(1, Math.round(Number(requestedStep) || 1)));
    if (!this.isStepLocked(step)) {
      return step;
    }
    for (let s = step; s >= 1; s--) {
      if (!this.isStepLocked(s)) {
        return s;
      }
    }
    return 1;
  }

  private reconcileRestoredOrganisationSelection(): void {
    const ids = this.form.get('organisationIds')?.value ?? [];
    if (!ids.length) {
      return;
    }

    this.organisationSelection = ids.map((id) => {
      const fromOptions = this.organisationOptions.find((org) => org.value === id);
      if (fromOptions) {
        return fromOptions;
      }
      const fromDraft = this.organisationSelection.find((org) => org.value === id);
      return fromDraft ?? { value: id, label: id };
    });
    this.reorderOrganisationOptions(ids);
  }

  private clearWizardDraft(): void {
    if (typeof sessionStorage === 'undefined') {
      return;
    }
    try {
      sessionStorage.removeItem(this.wizardDraftStorageKey);
    } catch {
      // Ignore private-mode errors
    }
    this.wizardDraftRestored = false;
  }

  private async resetWizardForNextPolicy(): Promise<void> {
    this.clearWizardDraft();
    this.form.reset({
      scopeType: 'group',
      organisationIds: [],
      targetRoles: [],
      individualName: '',
      individualEmail: '',
      assetKind: null,
      selectedAssetId: '',
      constraintApi: false,
      constraintFile: false,
      apiExpiryDate: '',
      apiExpiryTime: '',
      fileExpiryDate: '',
      fileExpiryTime: '',
      providerComment: '',
      feedbackComment: '',
    });

    this.currentStep = 1;
    this.roleSelection = [];
    this.organisationSelection = [];
    this.assetKindSelection = [];
    this.lastVerifiedIndividualEmail = '';
    this.selectedIndividualUserId = '';
    this.individualEmailOptions = [];
    this.clearIndividualUserLookupErrors();
    this.selectedAssetAccessTypes = [];
    this.clearAssetSelection();
    this.updateScopeValidators();
    this.syncAssetKindSelection();
    this.syncAllRowExpiryDateRangeModels();
    this.syncConstraintExpiryFieldStates();
    await this.applyDefaultOrganisationSelection();
    this.cdr.markForCheck();
  }
}
